// Types for Oposiciones module

export interface TestQuestion {
  id: number;
  text: string;
  imageUrl?: string;
  options: string[];
  optionImages?: string[];
  correct: number; // Index of correct answer (0-3)
  explanation: string;
  reference: string;
  difficulty?: 'facil' | 'media' | 'dificil';
  skill?: string;
  instructionShort?: string;
  realismTags?: string[];
  family?: string;
  whyCorrect?: string;
  distractorAnalysis?: string[];
  readingMeta?: ReadingMeta;
  /** Pregunta de reserva (no cuenta para la nota salvo anulación de una titular). */
  isReserve?: boolean;
}

export interface ReadingExpression {
  expression: string;
  meaning: string;
  usage?: string;
}

export interface ReadingMeta {
  title?: string;
  level?: string;
  textType?: string;
  mainIdea?: string;
  usefulExpressions?: ReadingExpression[];
  commonMistakes?: string[];
  spellingNotes?: string[];
  expressionNotes?: string[];
}

export interface TestConfig {
  topic: string;
  /** Número oficial del tema (1..N) — permite al backend cargar el índice curado de epígrafes. */
  topicNumber?: number;
  /** Slug del tema (ej. `iipp-27`) — fallback para el lookup en backend. */
  topicId?: string;
  questionCount: number;
  difficulty: 'facil' | 'media' | 'dificil';
  timeLimitMinutes: number | null; // null = sin límite
}

export interface TestSession {
  id: string;
  config: TestConfig;
  questions: TestQuestion[];
  answers: (number | null)[]; // User's answers
  currentIndex: number;
  startedAt: Date;
  completedAt?: Date;
  timeRemaining?: number; // seconds
}

export interface TestResult {
  testId: string;
  topic: string;
  score: number; // 0-100
  correctCount: number;
  totalQuestions: number;
  durationSeconds: number;
  timeLimitSeconds?: number;
  questions: TestQuestion[];
  answers: (number | null)[];
  completedAt: Date;
  /** ID del test original cuando éste es un re-intento. */
  parentTestId?: string;
  /** 1 = primer intento, 2 = segundo, etc. */
  attemptNumber?: number;
  /** Total de intentos guardados de este test (incluye el actual). */
  totalAttempts?: number;
}

export interface ResourceSearchResult {
  id: string;
  type: 'video' | 'document' | 'article';
  title: string;
  url: string;
  thumbnailUrl?: string;
  channelName?: string;
  duration?: string;
  aiSummary: string;
  relevanceScore: number;
  isVerified: boolean;
  topic?: string;
  videoId?: string;
  viewCount?: number;
  publishedAt?: string;
}

// === Estructura DUAL de caso práctico (alineada con SIAL 2025) ===

export interface CaseQuestion {
  number: number;
  text: string;
  points: number;
}

export interface BudgetRow {
  capitulo: string;
  previsionesDefinitivas?: number;
  derechosReconocidos?: number;
  recaudacionNeta?: number;
  creditosDefinitivos?: number;
  obligacionesReconocidas?: number;
  pagosRealizados?: number;
}

export interface JuridicalCase {
  antecedentes: string[];
  questions: CaseQuestion[];
  totalPoints: number;
}

export interface EconomicCase {
  context: string;
  budgetData: {
    ingresos: BudgetRow[];
    gastos: BudgetRow[];
  };
  accountingData: Record<string, number>;
  additionalInfo?: string;
  questions: CaseQuestion[];
  totalPoints: number;
}

export interface GeneratedCaseDual {
  title: string;
  topic: string;
  juridicalCase: JuridicalCase;
  economicCase: EconomicCase;
  keyPoints: string[];
  relevantLaws: string[];
}

// Legacy single-block format (backwards compat)
export interface GeneratedCaseLegacy {
  title: string;
  topic: string;
  caseText: string;
  questions: string[];
  keyPoints: string[];
  relevantLaws: string[];
}

export type GeneratedCase = GeneratedCaseDual | GeneratedCaseLegacy;

export function isDualCase(c: GeneratedCase): c is GeneratedCaseDual {
  return Boolean(
    'juridicalCase' in c && c.juridicalCase &&
    'economicCase' in c && c.economicCase,
  );
}

// === Evaluación por pregunta (profesor-evaluador) ===

export interface QuestionEvaluation {
  part: 'juridico' | 'economico';
  questionNumber: number;
  maxPoints: number;
  obtainedPoints: number;
  status: 'correcto' | 'parcial' | 'incorrecto';
  feedback: string;
  modelAnswer: string;
  missingReferences: string[];
}

export interface CaseEvaluationDual {
  score: number;       // 0-10 final grade
  maxScore: 10;
  totalPoints: number; // raw points obtained
  maxPoints: number;   // raw points max (24)
  grade: string;
  questionEvaluations: QuestionEvaluation[];
  strengths: string[];
  improvements: string[];
  preparadorTips: string[];
  modelAnswer: string;
}

// Legacy evaluation format (backwards compat)
export interface CaseEvaluationLegacy {
  score: number;
  maxScore: number;
  grade: string;
  strengths: string[];
  improvements: string[];
  missingReferences: string[];
  detailedFeedback: string;
  modelAnswer: string;
}

export type CaseEvaluation = CaseEvaluationDual | CaseEvaluationLegacy;

export function isDualEvaluation(e: CaseEvaluation): e is CaseEvaluationDual {
  return 'questionEvaluations' in e && 'preparadorTips' in e;
}

// === CasePractice (stored record) ===

export interface CasePractice {
  id: string;
  title: string;
  topic: string;
  caseText: string;
  questions?: string[];
  userAnswer?: string;
  aiFeedback?: {
    score: number;
    strengths: string[];
    improvements: string[];
    missingReferences: string[];
    questionEvaluations?: QuestionEvaluation[];
    preparadorTips?: string[];
    pathToPass?: string;
    pathToExcel?: string;
  };
  modelAnswer?: string;
  completedAt?: Date;
  /** ID del caso original cuando éste es un re-intento. */
  parentCaseId?: string;
  /** 1 = primer intento, 2 = segundo, etc. */
  attemptNumber?: number;
}

export interface OpositorProgress {
  totalTests: number;
  totalFlashcardsReviewed: number;
  totalCasesCompleted: number;
  streakDays: number;
  longestStreak: number;
  lastStudyDate?: Date;
  totalStudyMinutes: number;
  topicsMastery: Record<string, number>; // topic -> mastery percentage
  averageScore: number;
}

// Categorías de temas para organización
export type TopicCategory = 'comun' | 'especifico' | 'autonomica';

export interface OposicionTopic {
  id: string;
  name: string;
  icon: string;
  category: TopicCategory;
  temas?: string; // Rango de temas oficiales
}

// ============================================================================
// TEMARIO OFICIAL DE SECRETARÍA-INTERVENCIÓN
// 93 temas comunes + 52 específicos = 145 temas totales
// ============================================================================

export const OPOSICION_TOPICS: readonly OposicionTopic[] = [
  // === BLOQUE COMÚN: DERECHO CONSTITUCIONAL Y UE (Temas 1-16) ===
  { 
    id: 'derecho_constitucional', 
    name: 'Derecho Constitucional', 
    icon: '📜', 
    category: 'comun',
    temas: 'Temas 1-11: Constitución, Derechos fundamentales, Poderes del Estado'
  },
  { 
    id: 'union_europea', 
    name: 'Unión Europea', 
    icon: '🇪🇺', 
    category: 'comun',
    temas: 'Temas 14-16: Instituciones UE, Derecho comunitario, Comité de las Regiones'
  },
  
  // === BLOQUE COMÚN: DERECHO ADMINISTRATIVO (Temas 17-44) ===
  { 
    id: 'organizacion_admin', 
    name: 'Organización Administrativa', 
    icon: '🏛️', 
    category: 'comun',
    temas: 'Temas 17-19: Principio de legalidad, Potestad organizatoria, Competencia'
  },
  { 
    id: 'procedimiento_admin', 
    name: 'Procedimiento Administrativo (LPAC)', 
    icon: '📋', 
    category: 'comun',
    temas: 'Temas 20-38: Ley 39/2015, Actos, Notificaciones, Recursos'
  },
  { 
    id: 'transparencia', 
    name: 'Transparencia y Protección de Datos', 
    icon: '🔍', 
    category: 'comun',
    temas: 'Temas 23-24: Publicidad activa, Acceso a información, LOPDGDD'
  },
  { 
    id: 'jurisdiccion_contenciosa', 
    name: 'Jurisdicción Contencioso-Administrativa', 
    icon: '⚖️', 
    category: 'comun',
    temas: 'Temas 39-40: LJCA, Procedimiento, Recursos, Ejecución de sentencias'
  },
  { 
    id: 'potestad_sancionadora', 
    name: 'Potestad Sancionadora', 
    icon: '🚨', 
    category: 'comun',
    temas: 'Tema 41: Principios, Procedimiento sancionador'
  },
  { 
    id: 'expropiacion', 
    name: 'Expropiación Forzosa', 
    icon: '🏗️', 
    category: 'comun',
    temas: 'Tema 43: LEF, Procedimiento, Garantías, Reversión'
  },
  { 
    id: 'responsabilidad_patrimonial', 
    name: 'Responsabilidad Patrimonial', 
    icon: '💰', 
    category: 'comun',
    temas: 'Tema 44: Presupuestos, Procedimiento, Acción de regreso'
  },
  
  // === BLOQUE COMÚN: CONTRATACIÓN (Temas 45-56) ===
  { 
    id: 'contratacion', 
    name: 'Contratación del Sector Público', 
    icon: '📝', 
    category: 'comun',
    temas: 'Temas 45-51: LCSP, Tipos de contratos, Procedimientos de adjudicación'
  },
  { 
    id: 'contratos_tipicos', 
    name: 'Contratos Típicos (Obras, Servicios, Suministro)', 
    icon: '🔧', 
    category: 'comun',
    temas: 'Temas 52-56: Obras, Concesiones, Suministros, Servicios'
  },
  
  // === BLOQUE COMÚN: SERVICIOS Y PATRIMONIO (Temas 57-61) ===
  { 
    id: 'servicio_publico', 
    name: 'Servicio Público y Actividad Administrativa', 
    icon: '🏢', 
    category: 'comun',
    temas: 'Temas 57-58: Gestión de servicios, Policía, Fomento'
  },
  { 
    id: 'patrimonio', 
    name: 'Patrimonio de las AAPP', 
    icon: '🏛️', 
    category: 'comun',
    temas: 'Temas 59-61: Dominio público, Patrimonio privado, Dominios especiales'
  },
  
  // === BLOQUE COMÚN: HACIENDA GENERAL (Temas 62-66) ===
  { 
    id: 'hacienda_general', 
    name: 'Hacienda Pública y Sistema Tributario', 
    icon: '💵', 
    category: 'comun',
    temas: 'Temas 62-66: Financiación CCAA, Sistema tributario, Subvenciones'
  },
  
  // === BLOQUE COMÚN: FUNCIÓN PÚBLICA (Temas 67-83) ===
  { 
    id: 'funcion_publica', 
    name: 'Empleo Público (TREBEP)', 
    icon: '👔', 
    category: 'comun',
    temas: 'Temas 67-73: Clases de empleados, Acceso, Derechos, Deberes'
  },
  { 
    id: 'habilitados_nacionales', 
    name: 'Funcionarios Habilitación Nacional', 
    icon: '🎖️', 
    category: 'comun',
    temas: 'Temas 72-73: Funciones, Puestos reservados, Provisión'
  },
  { 
    id: 'derecho_laboral', 
    name: 'Derecho del Trabajo', 
    icon: '📄', 
    category: 'comun',
    temas: 'Temas 74-82: Estatuto de los Trabajadores, Convenios, Conflictos'
  },
  { 
    id: 'seguridad_social', 
    name: 'Seguridad Social', 
    icon: '🏥', 
    category: 'comun',
    temas: 'Tema 83: Régimen general, Acción protectora'
  },
  
  // === BLOQUE COMÚN: DERECHO MERCANTIL Y PENAL (Temas 84-88) ===
  { 
    id: 'derecho_mercantil', 
    name: 'Derecho Mercantil', 
    icon: '🏦', 
    category: 'comun',
    temas: 'Temas 84-86: Sociedades mercantiles, SA, SL'
  },
  { 
    id: 'delitos_funcionarios', 
    name: 'Delitos contra la Administración', 
    icon: '⚖️', 
    category: 'comun',
    temas: 'Temas 87-88: Urbanismo, Medio ambiente, Prevaricación'
  },
  
  // === BLOQUE COMÚN: GESTIÓN PÚBLICA (Temas 89-93) ===
  { 
    id: 'gestion_publica', 
    name: 'Gestión Pública y Administración Electrónica', 
    icon: '💻', 
    category: 'comun',
    temas: 'Temas 89-92: Agenda Digital, Smart cities, Ética, Compliance'
  },
  { 
    id: 'agenda_2030', 
    name: 'Agenda 2030 y ODS', 
    icon: '🌍', 
    category: 'comun',
    temas: 'Tema 93: Desarrollo sostenible, Implementación local'
  },
  
  // ============================================================================
  // BLOQUE ESPECÍFICO: RÉGIMEN LOCAL (52 temas)
  // ============================================================================
  
  // === RÉGIMEN LOCAL INSTITUCIONAL (Temas 1-9) ===
  { 
    id: 'regimen_local', 
    name: 'Régimen Local (LBRL)', 
    icon: '🏘️', 
    category: 'especifico',
    temas: 'Temas 1-6: Autonomía local, Municipio, Provincia, Organización'
  },
  { 
    id: 'elecciones_locales', 
    name: 'Sistema Electoral Local', 
    icon: '🗳️', 
    category: 'especifico',
    temas: 'Tema 7: Elección Concejales, Alcaldes, Diputados provinciales'
  },
  { 
    id: 'sesiones_acuerdos', 
    name: 'Sesiones y Acuerdos', 
    icon: '📋', 
    category: 'especifico',
    temas: 'Temas 8-9: Régimen de sesiones, Actas, Impugnación de acuerdos'
  },
  { 
    id: 'actividad_local', 
    name: 'Actividad Administrativa Local', 
    icon: '🏛️', 
    category: 'especifico',
    temas: 'Temas 10-12: Licencias, Servicios públicos locales'
  },
  
  // === HACIENDAS LOCALES (Temas 13-34) ===
  { 
    id: 'presupuestos_locales', 
    name: 'Presupuestos Locales', 
    icon: '📊', 
    category: 'especifico',
    temas: 'Temas 13-16: Estructura, Créditos, Ejecución, Liquidación'
  },
  { 
    id: 'estabilidad_presupuestaria', 
    name: 'Estabilidad Presupuestaria', 
    icon: '📈', 
    category: 'especifico',
    temas: 'Tema 17: Regla de gasto, Planes económico-financieros'
  },
  { 
    id: 'tesoreria_local', 
    name: 'Tesorería Local', 
    icon: '💳', 
    category: 'especifico',
    temas: 'Temas 18-19: Funciones, Pagos, Período medio de pago'
  },
  { 
    id: 'contabilidad_local', 
    name: 'Contabilidad Local', 
    icon: '📒', 
    category: 'especifico',
    temas: 'Temas 20-21: Instrucciones de contabilidad, Cuenta General'
  },
  { 
    id: 'control_interno', 
    name: 'Control Interno (Función interventora)', 
    icon: '🔎', 
    category: 'especifico',
    temas: 'Temas 22-24: Reparos, Control financiero, Auditoría, Tribunal de Cuentas'
  },
  { 
    id: 'tributos_locales', 
    name: 'Tributos Locales', 
    icon: '💰', 
    category: 'especifico',
    temas: 'Temas 25-32: IBI, IAE, IVTM, IIVTNU, Tasas, Contribuciones especiales'
  },
  { 
    id: 'financiacion_local', 
    name: 'Financiación y Crédito Local', 
    icon: '🏦', 
    category: 'especifico',
    temas: 'Temas 33-34: Participación en tributos, Operaciones de crédito'
  },
  
  // === URBANISMO (Temas 35-44) ===
  { 
    id: 'urbanismo_general', 
    name: 'Urbanismo (TRLSR)', 
    icon: '🏗️', 
    category: 'especifico',
    temas: 'Temas 35-38: Estatuto del suelo, Valoraciones, Situaciones básicas'
  },
  { 
    id: 'planeamiento', 
    name: 'Planeamiento Urbanístico', 
    icon: '📐', 
    category: 'especifico',
    temas: 'Temas 42-43: Planes, Ejecución, Sistemas de actuación'
  },
  { 
    id: 'disciplina_urbanistica', 
    name: 'Disciplina Urbanística', 
    icon: '🚧', 
    category: 'especifico',
    temas: 'Tema 44: Licencias, Protección de la legalidad'
  },
  { 
    id: 'vivienda', 
    name: 'Derecho a la Vivienda', 
    icon: '🏠', 
    category: 'especifico',
    temas: 'Temas 39-41: Patrimonio público de suelo, VPO'
  },
  
  // === COMPETENCIAS SECTORIALES (Temas 45-52) ===
  { 
    id: 'seguridad_ciudadana', 
    name: 'Seguridad Ciudadana y Policía Local', 
    icon: '🚔', 
    category: 'especifico',
    temas: 'Temas 45-46: Policías locales, Tráfico, Protección Civil'
  },
  { 
    id: 'medio_ambiente', 
    name: 'Medio Ambiente y Residuos', 
    icon: '🌿', 
    category: 'especifico',
    temas: 'Temas 47-49: Contaminación, Residuos, Aguas, Salubridad'
  },
  { 
    id: 'actividades_locales', 
    name: 'Actividades y Espectáculos', 
    icon: '🎭', 
    category: 'especifico',
    temas: 'Temas 50-51: Licencias de actividad, Comercio, Servicios sociales'
  },
  { 
    id: 'cultura_deporte', 
    name: 'Cultura, Educación y Turismo', 
    icon: '🎓', 
    category: 'especifico',
    temas: 'Tema 52: Patrimonio histórico, Deporte, Turismo'
  },
  
  // ============================================================================
  // NORMATIVA AUTONÓMICA
  // ============================================================================
  { 
    id: 'extremadura', 
    name: 'Normativa de Extremadura', 
    icon: '🟢', 
    category: 'autonomica',
    temas: 'Estatuto, LGAMEX, LOTUS, Función Pública'
  },
  { 
    id: 'andalucia', 
    name: 'Normativa de Andalucía', 
    icon: '🟡', 
    category: 'autonomica',
    temas: 'Estatuto, LAULA, LISTA, Función Pública'
  },
] as const;

export type OposicionTopicId = typeof OPOSICION_TOPICS[number]['id'];

// Helper para obtener temas por categoría
export const getTopicsByCategory = (category: TopicCategory): OposicionTopic[] => {
  return OPOSICION_TOPICS.filter(t => t.category === category) as OposicionTopic[];
};

// Helper para obtener el nombre completo del tema
export const getTopicFullName = (topicId: string): string => {
  const topic = OPOSICION_TOPICS.find(t => t.id === topicId);
  return topic ? `${topic.icon} ${topic.name}` : topicId;
};

// ============================================================================
// TEMARIO DETALLADO: 145 TEMAS INDIVIDUALES
// Para selector granular en TopicGeneratorModal
// ============================================================================

export interface DetailedTopic {
  id: string;
  number: number;
  name: string;
  block: string;
  blockId: string;
  category: 'comun' | 'especifico';
}

export const OPOSICION_TOPICS_DETAILED: DetailedTopic[] = [
  // ========== BLOQUE COMÚN ==========

  // --- Derecho Constitucional (Temas 1-11) ---
  { id: 'tc01', number: 1, name: 'La Constitución Española de 1978: estructura y contenido. Principios generales. Derechos y deberes fundamentales', block: 'Derecho Constitucional', blockId: 'derecho_constitucional', category: 'comun' },
  { id: 'tc02', number: 2, name: 'La Corona. Funciones constitucionales del Rey. Sucesión y Regencia', block: 'Derecho Constitucional', blockId: 'derecho_constitucional', category: 'comun' },
  { id: 'tc03', number: 3, name: 'Las Cortes Generales: composición, atribuciones y funcionamiento. El Defensor del Pueblo', block: 'Derecho Constitucional', blockId: 'derecho_constitucional', category: 'comun' },
  { id: 'tc04', number: 4, name: 'El Gobierno y la Administración. Relaciones del Gobierno con las Cortes Generales', block: 'Derecho Constitucional', blockId: 'derecho_constitucional', category: 'comun' },
  { id: 'tc05', number: 5, name: 'El Poder Judicial. El Tribunal Constitucional. El Tribunal de Cuentas', block: 'Derecho Constitucional', blockId: 'derecho_constitucional', category: 'comun' },
  { id: 'tc06', number: 6, name: 'La organización territorial del Estado. Las Comunidades Autónomas: constitución y competencias', block: 'Derecho Constitucional', blockId: 'derecho_constitucional', category: 'comun' },
  { id: 'tc07', number: 7, name: 'La Administración Local: principios constitucionales. La autonomía local', block: 'Derecho Constitucional', blockId: 'derecho_constitucional', category: 'comun' },
  { id: 'tc08', number: 8, name: 'La reforma constitucional. Procedimientos de reforma', block: 'Derecho Constitucional', blockId: 'derecho_constitucional', category: 'comun' },
  { id: 'tc09', number: 9, name: 'Los derechos fundamentales y las libertades públicas. Garantías y suspensión', block: 'Derecho Constitucional', blockId: 'derecho_constitucional', category: 'comun' },
  { id: 'tc10', number: 10, name: 'Los principios rectores de la política social y económica', block: 'Derecho Constitucional', blockId: 'derecho_constitucional', category: 'comun' },
  { id: 'tc11', number: 11, name: 'Igualdad y no discriminación. Políticas de igualdad de género', block: 'Derecho Constitucional', blockId: 'derecho_constitucional', category: 'comun' },

  // --- Organización del Estado (Temas 12-13) ---
  { id: 'tc12', number: 12, name: 'El Gobierno: composición, funciones y estatuto de sus miembros. La Ley del Gobierno', block: 'Organización del Estado', blockId: 'organizacion_estado', category: 'comun' },
  { id: 'tc13', number: 13, name: 'La Administración General del Estado. Organización y funcionamiento. Organismos públicos', block: 'Organización del Estado', blockId: 'organizacion_estado', category: 'comun' },

  // --- Unión Europea (Temas 14-16) ---
  { id: 'tc14', number: 14, name: 'La Unión Europea: origen, evolución y naturaleza jurídica. Instituciones de la UE', block: 'Unión Europea', blockId: 'union_europea', category: 'comun' },
  { id: 'tc15', number: 15, name: 'El Derecho comunitario: fuentes y relación con el ordenamiento jurídico de los Estados miembros', block: 'Unión Europea', blockId: 'union_europea', category: 'comun' },
  { id: 'tc16', number: 16, name: 'Las libertades comunitarias. Las políticas comunes. El Comité de las Regiones', block: 'Unión Europea', blockId: 'union_europea', category: 'comun' },

  // --- Organización Administrativa (Temas 17-19) ---
  { id: 'tc17', number: 17, name: 'El principio de legalidad. Las fuentes del Derecho Administrativo. La jerarquía normativa', block: 'Organización Administrativa', blockId: 'organizacion_admin', category: 'comun' },
  { id: 'tc18', number: 18, name: 'La potestad organizatoria. Los órganos administrativos: concepto y clases. Órganos colegiados', block: 'Organización Administrativa', blockId: 'organizacion_admin', category: 'comun' },
  { id: 'tc19', number: 19, name: 'La competencia administrativa: concepto, clases y criterios de delimitación. Delegación, avocación, encomienda de gestión, delegación de firma y suplencia', block: 'Organización Administrativa', blockId: 'organizacion_admin', category: 'comun' },

  // --- Procedimiento Administrativo (Temas 20-38) ---
  { id: 'tc20', number: 20, name: 'La Ley 39/2015: ámbito de aplicación y principios informadores', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc21', number: 21, name: 'Los interesados en el procedimiento. Capacidad de obrar y representación', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc22', number: 22, name: 'La actividad de las Administraciones Públicas. Derechos de los ciudadanos. Obligaciones de transparencia', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc23', number: 23, name: 'Transparencia, acceso a la información pública y buen gobierno. Publicidad activa y derecho de acceso', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc24', number: 24, name: 'Protección de datos de carácter personal. LOPDGDD y RGPD', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc25', number: 25, name: 'El acto administrativo: concepto, clases y elementos. La motivación y la notificación', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc26', number: 26, name: 'La eficacia del acto administrativo. Ejecutividad y ejecutoriedad. Suspensión', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc27', number: 27, name: 'La invalidez del acto administrativo. Nulidad de pleno derecho y anulabilidad. La conversión, conservación y convalidación', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc28', number: 28, name: 'Disposiciones generales sobre los procedimientos administrativos. Iniciación, ordenación, instrucción y finalización', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc29', number: 29, name: 'Los plazos administrativos: cómputo, ampliación y tramitación de urgencia', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc30', number: 30, name: 'El silencio administrativo: régimen jurídico. Silencio positivo y negativo', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc31', number: 31, name: 'La revisión de oficio. Declaración de lesividad. Revocación de actos', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc32', number: 32, name: 'Los recursos administrativos: alzada, reposición y extraordinario de revisión', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc33', number: 33, name: 'Las reclamaciones económico-administrativas. Reclamaciones previas a la vía civil y laboral', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc34', number: 34, name: 'La administración electrónica. Sede electrónica, registro, notificación y archivo electrónicos', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc35', number: 35, name: 'La Ley 40/2015 (LRJSP): principios generales. Funcionamiento electrónico del sector público', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc36', number: 36, name: 'Los convenios administrativos. Encomiendas de gestión. Delegación interorgánica e intersubjetiva', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc37', number: 37, name: 'La potestad reglamentaria. Iniciativa legislativa y potestad normativa de las Administraciones', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },
  { id: 'tc38', number: 38, name: 'Los contratos del sector público: delimitación de los tipos contractuales. Contratos sujetos a regulación armonizada', block: 'Procedimiento Administrativo', blockId: 'procedimiento_admin', category: 'comun' },

  // --- Jurisdicción Contencioso-Administrativa (Temas 39-40) ---
  { id: 'tc39', number: 39, name: 'La jurisdicción contencioso-administrativa: naturaleza, extensión y límites. Órganos y competencias', block: 'Jurisdicción Contencioso-Administrativa', blockId: 'jurisdiccion_contenciosa', category: 'comun' },
  { id: 'tc40', number: 40, name: 'El recurso contencioso-administrativo: procedimiento ordinario y abreviado. Recursos y ejecución de sentencias', block: 'Jurisdicción Contencioso-Administrativa', blockId: 'jurisdiccion_contenciosa', category: 'comun' },

  // --- Potestad Sancionadora y Responsabilidad (Temas 41-44) ---
  { id: 'tc41', number: 41, name: 'La potestad sancionadora: principios generales. Procedimiento sancionador', block: 'Potestad Sancionadora y Responsabilidad', blockId: 'potestad_sancionadora', category: 'comun' },
  { id: 'tc42', number: 42, name: 'La responsabilidad de las Administraciones Públicas. Responsabilidad de las autoridades y personal', block: 'Potestad Sancionadora y Responsabilidad', blockId: 'potestad_sancionadora', category: 'comun' },
  { id: 'tc43', number: 43, name: 'La expropiación forzosa: sujetos, objeto y causa. Procedimiento general. Garantías jurisdiccionales. Reversión', block: 'Potestad Sancionadora y Responsabilidad', blockId: 'potestad_sancionadora', category: 'comun' },
  { id: 'tc44', number: 44, name: 'Responsabilidad patrimonial de la Administración: presupuestos, procedimiento y acción de regreso', block: 'Potestad Sancionadora y Responsabilidad', blockId: 'potestad_sancionadora', category: 'comun' },

  // --- Contratación del Sector Público (Temas 45-56) ---
  { id: 'tc45', number: 45, name: 'La LCSP: ámbito de aplicación. Negocios y contratos excluidos. Jurisdicción competente', block: 'Contratación del Sector Público', blockId: 'contratacion', category: 'comun' },
  { id: 'tc46', number: 46, name: 'Requisitos para contratar con el sector público. Capacidad y solvencia. Prohibiciones de contratar. Clasificación empresarial', block: 'Contratación del Sector Público', blockId: 'contratacion', category: 'comun' },
  { id: 'tc47', number: 47, name: 'Objeto, precio y cuantía del contrato. Garantías exigibles. El expediente de contratación', block: 'Contratación del Sector Público', blockId: 'contratacion', category: 'comun' },
  { id: 'tc48', number: 48, name: 'Procedimientos de adjudicación: abierto, restringido, negociado, diálogo competitivo y asociación para la innovación', block: 'Contratación del Sector Público', blockId: 'contratacion', category: 'comun' },
  { id: 'tc49', number: 49, name: 'El contrato menor. Procedimiento simplificado. Racionalización técnica de la contratación', block: 'Contratación del Sector Público', blockId: 'contratacion', category: 'comun' },
  { id: 'tc50', number: 50, name: 'Efectos, cumplimiento y extinción de los contratos. Prerrogativas de la Administración. Modificación contractual', block: 'Contratación del Sector Público', blockId: 'contratacion', category: 'comun' },
  { id: 'tc51', number: 51, name: 'Cesión, subcontratación y resolución de los contratos. Recurso especial en materia de contratación', block: 'Contratación del Sector Público', blockId: 'contratacion', category: 'comun' },
  { id: 'tc52', number: 52, name: 'El contrato de obras: actuaciones preparatorias, ejecución, modificación, cumplimiento y resolución', block: 'Contratación del Sector Público', blockId: 'contratacion', category: 'comun' },
  { id: 'tc53', number: 53, name: 'El contrato de concesión de obras: régimen jurídico, ejecución y extinción', block: 'Contratación del Sector Público', blockId: 'contratacion', category: 'comun' },
  { id: 'tc54', number: 54, name: 'El contrato de concesión de servicios: régimen jurídico y extinción', block: 'Contratación del Sector Público', blockId: 'contratacion', category: 'comun' },
  { id: 'tc55', number: 55, name: 'El contrato de suministro: régimen jurídico, ejecución y extinción', block: 'Contratación del Sector Público', blockId: 'contratacion', category: 'comun' },
  { id: 'tc56', number: 56, name: 'El contrato de servicios: régimen jurídico, ejecución y extinción', block: 'Contratación del Sector Público', blockId: 'contratacion', category: 'comun' },

  // --- Servicio Público y Patrimonio (Temas 57-61) ---
  { id: 'tc57', number: 57, name: 'El servicio público: concepto, evolución y régimen jurídico. Formas de gestión', block: 'Servicio Público y Patrimonio', blockId: 'servicio_publico', category: 'comun' },
  { id: 'tc58', number: 58, name: 'Formas de actividad administrativa: policía, fomento y servicio público', block: 'Servicio Público y Patrimonio', blockId: 'servicio_publico', category: 'comun' },
  { id: 'tc59', number: 59, name: 'El dominio público: concepto, naturaleza y elementos. Afectación y desafectación. Uso y utilización', block: 'Servicio Público y Patrimonio', blockId: 'servicio_publico', category: 'comun' },
  { id: 'tc60', number: 60, name: 'El patrimonio privado de las Administraciones Públicas. Régimen jurídico', block: 'Servicio Público y Patrimonio', blockId: 'servicio_publico', category: 'comun' },
  { id: 'tc61', number: 61, name: 'Dominios públicos especiales: aguas, minas, montes, costas y patrimonio histórico', block: 'Servicio Público y Patrimonio', blockId: 'servicio_publico', category: 'comun' },

  // --- Hacienda Pública General (Temas 62-66) ---
  { id: 'tc62', number: 62, name: 'La financiación de las Comunidades Autónomas. LOFCA. El sistema de financiación actual', block: 'Hacienda Pública General', blockId: 'hacienda_general', category: 'comun' },
  { id: 'tc63', number: 63, name: 'El sistema tributario español. Principios constitucionales. La Ley General Tributaria', block: 'Hacienda Pública General', blockId: 'hacienda_general', category: 'comun' },
  { id: 'tc64', number: 64, name: 'Los impuestos estatales directos: IRPF, IS, IP, ISD', block: 'Hacienda Pública General', blockId: 'hacienda_general', category: 'comun' },
  { id: 'tc65', number: 65, name: 'Los impuestos estatales indirectos: IVA, ITPAJD, Impuestos Especiales', block: 'Hacienda Pública General', blockId: 'hacienda_general', category: 'comun' },
  { id: 'tc66', number: 66, name: 'Las subvenciones públicas. Ley General de Subvenciones. Procedimiento de concesión y reintegro', block: 'Hacienda Pública General', blockId: 'hacienda_general', category: 'comun' },

  // --- Función Pública (Temas 67-73) ---
  { id: 'tc67', number: 67, name: 'El personal al servicio de las AAPP: clases y régimen jurídico. El TREBEP', block: 'Función Pública', blockId: 'funcion_publica', category: 'comun' },
  { id: 'tc68', number: 68, name: 'Acceso al empleo público: principios, sistemas selectivos y provisión de puestos', block: 'Función Pública', blockId: 'funcion_publica', category: 'comun' },
  { id: 'tc69', number: 69, name: 'Derechos y deberes de los empleados públicos. Código de conducta. Régimen de incompatibilidades', block: 'Función Pública', blockId: 'funcion_publica', category: 'comun' },
  { id: 'tc70', number: 70, name: 'Situaciones administrativas. Régimen disciplinario. Extinción de la relación de servicio', block: 'Función Pública', blockId: 'funcion_publica', category: 'comun' },
  { id: 'tc71', number: 71, name: 'Retribuciones de los funcionarios públicos. Estructura retributiva. Negociación colectiva', block: 'Función Pública', blockId: 'funcion_publica', category: 'comun' },
  { id: 'tc72', number: 72, name: 'Los funcionarios de Administración Local con habilitación de carácter nacional: funciones y categorías', block: 'Función Pública', blockId: 'funcion_publica', category: 'comun' },
  { id: 'tc73', number: 73, name: 'Régimen de provisión de puestos reservados a habilitados nacionales. Funciones de Secretaría, Intervención y Tesorería', block: 'Función Pública', blockId: 'funcion_publica', category: 'comun' },

  // --- Derecho del Trabajo (Temas 74-82) ---
  { id: 'tc74', number: 74, name: 'El Derecho del Trabajo: fuentes. El Estatuto de los Trabajadores', block: 'Derecho del Trabajo', blockId: 'derecho_laboral', category: 'comun' },
  { id: 'tc75', number: 75, name: 'El contrato de trabajo: concepto, elementos y clases. Modalidades de contratación', block: 'Derecho del Trabajo', blockId: 'derecho_laboral', category: 'comun' },
  { id: 'tc76', number: 76, name: 'Derechos y deberes del trabajador. El salario y el tiempo de trabajo', block: 'Derecho del Trabajo', blockId: 'derecho_laboral', category: 'comun' },
  { id: 'tc77', number: 77, name: 'Modificación, suspensión y extinción del contrato de trabajo', block: 'Derecho del Trabajo', blockId: 'derecho_laboral', category: 'comun' },
  { id: 'tc78', number: 78, name: 'La negociación colectiva: convenios colectivos. Representación de los trabajadores', block: 'Derecho del Trabajo', blockId: 'derecho_laboral', category: 'comun' },
  { id: 'tc79', number: 79, name: 'Los conflictos colectivos. La huelga y el cierre patronal', block: 'Derecho del Trabajo', blockId: 'derecho_laboral', category: 'comun' },
  { id: 'tc80', number: 80, name: 'El proceso laboral: principios informadores y modalidades procesales', block: 'Derecho del Trabajo', blockId: 'derecho_laboral', category: 'comun' },
  { id: 'tc81', number: 81, name: 'Prevención de riesgos laborales: principios generales. Derechos y obligaciones', block: 'Derecho del Trabajo', blockId: 'derecho_laboral', category: 'comun' },
  { id: 'tc82', number: 82, name: 'El personal laboral al servicio de las Entidades Locales. Convenios colectivos del ámbito local', block: 'Derecho del Trabajo', blockId: 'derecho_laboral', category: 'comun' },

  // --- Seguridad Social (Tema 83) ---
  { id: 'tc83', number: 83, name: 'La Seguridad Social: régimen general. Acción protectora: prestaciones', block: 'Seguridad Social', blockId: 'seguridad_social', category: 'comun' },

  // --- Derecho Mercantil (Temas 84-86) ---
  { id: 'tc84', number: 84, name: 'El comerciante. El Registro Mercantil. Las sociedades mercantiles: concepto y clases', block: 'Derecho Mercantil', blockId: 'derecho_mercantil', category: 'comun' },
  { id: 'tc85', number: 85, name: 'La sociedad anónima: constitución, órganos, acciones y disolución', block: 'Derecho Mercantil', blockId: 'derecho_mercantil', category: 'comun' },
  { id: 'tc86', number: 86, name: 'La sociedad de responsabilidad limitada. Otras formas societarias. La sociedad cooperativa', block: 'Derecho Mercantil', blockId: 'derecho_mercantil', category: 'comun' },

  // --- Delitos contra la Administración (Temas 87-88) ---
  { id: 'tc87', number: 87, name: 'Delitos contra la Administración Pública: prevaricación, cohecho, malversación, tráfico de influencias', block: 'Delitos contra la Administración', blockId: 'delitos_funcionarios', category: 'comun' },
  { id: 'tc88', number: 88, name: 'Delitos contra la ordenación del territorio, el urbanismo y el medio ambiente', block: 'Delitos contra la Administración', blockId: 'delitos_funcionarios', category: 'comun' },

  // --- Gestión Pública (Temas 89-93) ---
  { id: 'tc89', number: 89, name: 'La Administración electrónica. El esquema nacional de interoperabilidad y seguridad', block: 'Gestión Pública', blockId: 'gestion_publica', category: 'comun' },
  { id: 'tc90', number: 90, name: 'Gobierno abierto y smart cities. Innovación en la gestión pública local', block: 'Gestión Pública', blockId: 'gestion_publica', category: 'comun' },
  { id: 'tc91', number: 91, name: 'Ética pública y buen gobierno. Compliance en el sector público', block: 'Gestión Pública', blockId: 'gestion_publica', category: 'comun' },
  { id: 'tc92', number: 92, name: 'La calidad en los servicios públicos. Evaluación del desempeño y gestión por objetivos', block: 'Gestión Pública', blockId: 'gestion_publica', category: 'comun' },
  { id: 'tc93', number: 93, name: 'La Agenda 2030 y los Objetivos de Desarrollo Sostenible. Implementación en el ámbito local', block: 'Gestión Pública', blockId: 'gestion_publica', category: 'comun' },

  // ========== BLOQUE ESPECÍFICO ==========

  // --- Régimen Local (Temas E1-E9) ---
  { id: 'te01', number: 1, name: 'El régimen local español: principios constitucionales y regulación jurídica. La LBRL', block: 'Régimen Local', blockId: 'regimen_local', category: 'especifico' },
  { id: 'te02', number: 2, name: 'El Municipio: concepto, elementos y competencias. La autonomía municipal', block: 'Régimen Local', blockId: 'regimen_local', category: 'especifico' },
  { id: 'te03', number: 3, name: 'La organización municipal: el Alcalde, el Pleno, la Junta de Gobierno Local y las Comisiones Informativas', block: 'Régimen Local', blockId: 'regimen_local', category: 'especifico' },
  { id: 'te04', number: 4, name: 'Competencias municipales. Servicios mínimos obligatorios. Delegación de competencias', block: 'Régimen Local', blockId: 'regimen_local', category: 'especifico' },
  { id: 'te05', number: 5, name: 'La Provincia: organización, competencias y régimen jurídico. Las Diputaciones Provinciales', block: 'Régimen Local', blockId: 'regimen_local', category: 'especifico' },
  { id: 'te06', number: 6, name: 'Otras entidades locales: comarcas, mancomunidades, áreas metropolitanas y entidades locales menores', block: 'Régimen Local', blockId: 'regimen_local', category: 'especifico' },
  { id: 'te07', number: 7, name: 'El sistema electoral local. Elección de Concejales, Alcalde y Diputados provinciales. La moción de censura', block: 'Régimen Local', blockId: 'regimen_local', category: 'especifico' },
  { id: 'te08', number: 8, name: 'El régimen de sesiones de los órganos colegiados locales. Convocatoria, quórum y mayorías. Las actas', block: 'Régimen Local', blockId: 'regimen_local', category: 'especifico' },
  { id: 'te09', number: 9, name: 'Los acuerdos locales: impugnación y revisión. Conflictos de atribuciones. El ROF', block: 'Régimen Local', blockId: 'regimen_local', category: 'especifico' },

  // --- Actividad Administrativa Local (Temas E10-E12) ---
  { id: 'te10', number: 10, name: 'La actividad de fomento en la esfera local. Las subvenciones locales', block: 'Actividad Administrativa Local', blockId: 'actividad_local', category: 'especifico' },
  { id: 'te11', number: 11, name: 'Los servicios públicos locales: formas de gestión directa e indirecta', block: 'Actividad Administrativa Local', blockId: 'actividad_local', category: 'especifico' },
  { id: 'te12', number: 12, name: 'Las licencias y autorizaciones administrativas. La comunicación previa y declaración responsable', block: 'Actividad Administrativa Local', blockId: 'actividad_local', category: 'especifico' },

  // --- Presupuestos Locales (Temas E13-E17) ---
  { id: 'te13', number: 13, name: 'El Presupuesto General de las Entidades Locales: concepto, estructura y principios. Elaboración y aprobación', block: 'Haciendas Locales', blockId: 'presupuestos_locales', category: 'especifico' },
  { id: 'te14', number: 14, name: 'Los créditos y sus modificaciones: créditos extraordinarios, suplementos, ampliaciones, transferencias y generación de créditos', block: 'Haciendas Locales', blockId: 'presupuestos_locales', category: 'especifico' },
  { id: 'te15', number: 15, name: 'La ejecución del presupuesto de gastos y de ingresos. Fases del procedimiento', block: 'Haciendas Locales', blockId: 'presupuestos_locales', category: 'especifico' },
  { id: 'te16', number: 16, name: 'La liquidación del Presupuesto. Resultado presupuestario y remanente de tesorería', block: 'Haciendas Locales', blockId: 'presupuestos_locales', category: 'especifico' },
  { id: 'te17', number: 17, name: 'Estabilidad presupuestaria y sostenibilidad financiera. Regla de gasto. Planes económico-financieros y de ajuste', block: 'Haciendas Locales', blockId: 'presupuestos_locales', category: 'especifico' },

  // --- Tesorería Local (Temas E18-E19) ---
  { id: 'te18', number: 18, name: 'La Tesorería de las Entidades Locales: funciones, organización y régimen jurídico', block: 'Haciendas Locales', blockId: 'tesoreria_local', category: 'especifico' },
  { id: 'te19', number: 19, name: 'La gestión recaudatoria. Período voluntario y ejecutivo. El período medio de pago a proveedores', block: 'Haciendas Locales', blockId: 'tesoreria_local', category: 'especifico' },

  // --- Contabilidad Local (Temas E20-E21) ---
  { id: 'te20', number: 20, name: 'La contabilidad de las Entidades Locales. Instrucciones de contabilidad local. La Cuenta General', block: 'Haciendas Locales', blockId: 'contabilidad_local', category: 'especifico' },
  { id: 'te21', number: 21, name: 'El Plan General de Contabilidad Pública adaptado a la Administración Local', block: 'Haciendas Locales', blockId: 'contabilidad_local', category: 'especifico' },

  // --- Control Interno (Temas E22-E24) ---
  { id: 'te22', number: 22, name: 'El control interno de la actividad económico-financiera local. La función interventora: régimen jurídico y ámbito', block: 'Haciendas Locales', blockId: 'control_interno', category: 'especifico' },
  { id: 'te23', number: 23, name: 'El control financiero y la auditoría pública en el ámbito local. El informe de control financiero', block: 'Haciendas Locales', blockId: 'control_interno', category: 'especifico' },
  { id: 'te24', number: 24, name: 'El Tribunal de Cuentas y los órganos de control externo de las CCAA. La responsabilidad contable', block: 'Haciendas Locales', blockId: 'control_interno', category: 'especifico' },

  // --- Tributos Locales (Temas E25-E32) ---
  { id: 'te25', number: 25, name: 'Las Haciendas Locales: clasificación de los recursos. Régimen jurídico del TRLRHL', block: 'Haciendas Locales', blockId: 'tributos_locales', category: 'especifico' },
  { id: 'te26', number: 26, name: 'El Impuesto sobre Bienes Inmuebles (IBI): hecho imponible, sujeto pasivo, base imponible y cuota', block: 'Haciendas Locales', blockId: 'tributos_locales', category: 'especifico' },
  { id: 'te27', number: 27, name: 'El Impuesto sobre Actividades Económicas (IAE): hecho imponible, exenciones, cuota y gestión', block: 'Haciendas Locales', blockId: 'tributos_locales', category: 'especifico' },
  { id: 'te28', number: 28, name: 'El Impuesto sobre Vehículos de Tracción Mecánica (IVTM): régimen jurídico', block: 'Haciendas Locales', blockId: 'tributos_locales', category: 'especifico' },
  { id: 'te29', number: 29, name: 'El Impuesto sobre el Incremento de Valor de los Terrenos (IIVTNU): hecho imponible, base imponible y gestión', block: 'Haciendas Locales', blockId: 'tributos_locales', category: 'especifico' },
  { id: 'te30', number: 30, name: 'El Impuesto sobre Construcciones, Instalaciones y Obras (ICIO): régimen jurídico', block: 'Haciendas Locales', blockId: 'tributos_locales', category: 'especifico' },
  { id: 'te31', number: 31, name: 'Las tasas locales: hecho imponible, cuantificación y gestión. Los precios públicos', block: 'Haciendas Locales', blockId: 'tributos_locales', category: 'especifico' },
  { id: 'te32', number: 32, name: 'Las contribuciones especiales locales: hecho imponible, sujeto pasivo, base imponible y cuota', block: 'Haciendas Locales', blockId: 'tributos_locales', category: 'especifico' },

  // --- Financiación Local (Temas E33-E34) ---
  { id: 'te33', number: 33, name: 'Las participaciones en tributos del Estado y de las CCAA. El Fondo Complementario de Financiación', block: 'Haciendas Locales', blockId: 'financiacion_local', category: 'especifico' },
  { id: 'te34', number: 34, name: 'Las operaciones de crédito de las Entidades Locales. Régimen de endeudamiento. Tutela financiera', block: 'Haciendas Locales', blockId: 'financiacion_local', category: 'especifico' },

  // --- Urbanismo (Temas E35-E44) ---
  { id: 'te35', number: 35, name: 'El Derecho urbanístico: concepto y evolución. Distribución de competencias. TRLSR 2015', block: 'Urbanismo', blockId: 'urbanismo', category: 'especifico' },
  { id: 'te36', number: 36, name: 'El estatuto jurídico de la propiedad del suelo. Situaciones básicas: suelo rural y urbanizado', block: 'Urbanismo', blockId: 'urbanismo', category: 'especifico' },
  { id: 'te37', number: 37, name: 'Las valoraciones del suelo. Criterios generales de valoración. Indemnizaciones', block: 'Urbanismo', blockId: 'urbanismo', category: 'especifico' },
  { id: 'te38', number: 38, name: 'La expropiación urbanística. Supuestos expropiatorios y procedimiento', block: 'Urbanismo', blockId: 'urbanismo', category: 'especifico' },
  { id: 'te39', number: 39, name: 'El patrimonio público de suelo. Derecho de superficie. Viviendas de protección oficial', block: 'Urbanismo', blockId: 'urbanismo', category: 'especifico' },
  { id: 'te40', number: 40, name: 'El derecho a la vivienda. Políticas de vivienda y suelo', block: 'Urbanismo', blockId: 'urbanismo', category: 'especifico' },
  { id: 'te41', number: 41, name: 'Los instrumentos de planeamiento urbanístico. Planes generales, parciales y especiales', block: 'Urbanismo', blockId: 'urbanismo', category: 'especifico' },
  { id: 'te42', number: 42, name: 'La ejecución del planeamiento. Sistemas de actuación. Juntas de compensación', block: 'Urbanismo', blockId: 'urbanismo', category: 'especifico' },
  { id: 'te43', number: 43, name: 'Las licencias urbanísticas. Actos sujetos a licencia. La comunicación previa en urbanismo', block: 'Urbanismo', blockId: 'urbanismo', category: 'especifico' },
  { id: 'te44', number: 44, name: 'La disciplina urbanística. Protección de la legalidad. Infracciones y sanciones', block: 'Urbanismo', blockId: 'urbanismo', category: 'especifico' },

  // --- Competencias Sectoriales (Temas E45-E52) ---
  { id: 'te45', number: 45, name: 'La seguridad ciudadana. La policía local: organización y funciones', block: 'Competencias Sectoriales', blockId: 'competencias_sectoriales', category: 'especifico' },
  { id: 'te46', number: 46, name: 'Tráfico, circulación y seguridad vial. Protección civil en el ámbito local', block: 'Competencias Sectoriales', blockId: 'competencias_sectoriales', category: 'especifico' },
  { id: 'te47', number: 47, name: 'La protección del medio ambiente. Evaluación de impacto ambiental. Contaminación atmosférica y acústica', block: 'Competencias Sectoriales', blockId: 'competencias_sectoriales', category: 'especifico' },
  { id: 'te48', number: 48, name: 'La gestión de residuos. Abastecimiento y saneamiento de aguas. Salubridad pública', block: 'Competencias Sectoriales', blockId: 'competencias_sectoriales', category: 'especifico' },
  { id: 'te49', number: 49, name: 'Las aguas: dominio público hidráulico. Abastecimiento y saneamiento municipal', block: 'Competencias Sectoriales', blockId: 'competencias_sectoriales', category: 'especifico' },
  { id: 'te50', number: 50, name: 'Actividades clasificadas y de espectáculos públicos. Licencias de apertura y funcionamiento', block: 'Competencias Sectoriales', blockId: 'competencias_sectoriales', category: 'especifico' },
  { id: 'te51', number: 51, name: 'El comercio local. Mercados municipales. Los servicios sociales en el ámbito local', block: 'Competencias Sectoriales', blockId: 'competencias_sectoriales', category: 'especifico' },
  { id: 'te52', number: 52, name: 'El patrimonio histórico y cultural. Competencias locales en educación, deporte y turismo', block: 'Competencias Sectoriales', blockId: 'competencias_sectoriales', category: 'especifico' },
];

// Helper para obtener bloques únicos con sus temas
export const getDetailedTopicBlocks = (category?: 'comun' | 'especifico'): { blockId: string; block: string; topics: DetailedTopic[] }[] => {
  const filtered = category
    ? OPOSICION_TOPICS_DETAILED.filter(t => t.category === category)
    : OPOSICION_TOPICS_DETAILED;

  const blockMap = new Map<string, { block: string; topics: DetailedTopic[] }>();

  for (const topic of filtered) {
    if (!blockMap.has(topic.blockId)) {
      blockMap.set(topic.blockId, { block: topic.block, topics: [] });
    }
    blockMap.get(topic.blockId)!.topics.push(topic);
  }

  return Array.from(blockMap.entries()).map(([blockId, data]) => ({
    blockId,
    ...data,
  }));
};

// Helper para buscar temas por texto o número
export const searchDetailedTopics = (query: string): DetailedTopic[] => {
  if (!query.trim()) return OPOSICION_TOPICS_DETAILED;
  const lower = query.toLowerCase();
  const num = parseInt(query, 10);

  return OPOSICION_TOPICS_DETAILED.filter(t =>
    t.name.toLowerCase().includes(lower) ||
    t.block.toLowerCase().includes(lower) ||
    (!isNaN(num) && t.number === num)
  );
};
